import { KeyboardArrowDownRounded } from "@mui/icons-material";
import {
  Button,
  type ButtonProps,
  Popover,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { SubMenuButtonProps } from '@monthly/ui' */
export interface SubMenuButtonProps extends Exclude<ButtonProps, "endIcon"> {
  /**
   * Array of submenu items, each with a name, onClick handler, and optional disabled state. If only one item is provided,
   * it will be executed directly on button click without showing the popover.
   */
  items: {
    name: string;
    onClick: () => void;
    disabled?: boolean;
  }[];
}

const ButtonStyled = styled(Button)(({ theme, disabled }) => ({
  transition: "all 0.30s ease",
  gap: "4px",
  width: "fit-content",
  height: "34px",
  borderRadius: "4px",
  border: `1px solid ${theme.palette.secondary[50]}`,
  background: theme.palette.support[50],
  color: disabled ? theme.palette.secondary[100] : theme.palette.secondary.main,
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
  },
  "& .MuiButton-startIcon": {
    margin: 0,
  },
  "&:hover": {
    backgroundColor: theme.palette.support[200],
  },
  "&:active": { backgroundColor: theme.palette.support[100] },
}));

const StyledSubButton = styled(Button)(({ theme }) => ({
  justifyContent: "flex-start",
  padding: 0,
  width: "100%",
  borderRadius: 0,
  "&:hover": {
    backgroundColor: "transparent",
    ".MuiTypography-root": {
      color: theme.palette.secondary.dark,
    },
  },
}));

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { SubMenuButton } from '@monthly/ui' */
export function SubMenuButton({
  items,
  children,
  ...props
}: SubMenuButtonProps) {
  const [open, setOpen] = useState(false);
  const anchorElRef = useRef(null);

  const handleClose = () => {
    setOpen(false);
  };

  const handleItemClick = (onClick: () => void) => () => {
    onClick();
    handleClose();
  };

  const handleButtonClick = () => {
    if (items.length === 1) {
      if (items[0]?.disabled) return;
      return items[0].onClick();
    }
    setOpen((currentState) => !currentState);
  };

  return (
    <>
      <ButtonStyled
        disableRipple
        disableFocusRipple
        ref={anchorElRef}
        onClick={handleButtonClick}
        {...props}
      >
        <Typography
          sx={(theme) => ({
            fontSize: 13,
            fontWeight: theme.typography.fontWeightSemiBold,
            color: props.disabled
              ? theme.palette.secondary[100]
              : theme.palette.secondary.main,
          })}
        >
          {children}
        </Typography>
        <KeyboardArrowDownRounded />
      </ButtonStyled>

      {open && (
        <Popover
          anchorEl={anchorElRef.current}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          slotProps={{
            paper: {
              sx: (theme) => ({
                marginTop: "12px",
                padding: "12px 8px",
                backgroundColor: theme.palette.support[50],
                boxShadow: theme.shadows[4],
                borderRadius: "4px",
              }),
            },
          }}
        >
          <Stack gap={1}>
            {items.map(({ name, onClick, disabled }) => (
              <StyledSubButton
                key={name}
                onClick={handleItemClick(onClick)}
                disabled={disabled}
                disableRipple
              >
                <Typography
                  sx={(theme) => ({
                    fontSize: 13,
                    fontWeight: theme.typography.fontWeightSemiBold,
                    color: disabled
                      ? theme.palette.secondary[100]
                      : theme.palette.secondary.main,
                  })}
                >
                  {name}
                </Typography>
              </StyledSubButton>
            ))}
          </Stack>
        </Popover>
      )}
    </>
  );
}
